import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener('pointerdown', this.#removeVariantMediaPreview, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
        newMediaGallery.showSelectedVariantMediaPreview();
        newMediaGallery.scrollIntoViewOnMobile();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Shows the chosen variant as a temporary overlay on the first general-media
   * slide. Variant media never becomes a carousel slide, so normal navigation
   * remains limited to the product's unassigned media.
   */
  showSelectedVariantMediaPreview() {
    const { selectedVariantMediaUrl: src, selectedVariantMediaAlt: alt = '' } = this.dataset;
    if (!src) return;

    requestAnimationFrame(() => {
      const slide = this.querySelector('slideshow-slide[aria-hidden="false"]') || this.querySelector('slideshow-slide');
      if (!slide || slide.querySelector('.product-media-container__variant-preview')) return;

      const image = new Image();
      image.className = 'product-media-container__variant-preview';
      image.src = src;
      image.alt = alt;
      image.decoding = 'async';
      slide.append(image);
    });
  }

  #removeVariantMediaPreview = () => {
    this.querySelector('.product-media-container__variant-preview')?.remove();
  };

  /**
   * After a buyer selects a variant on a phone, bring the refreshed gallery
   * back into view. CSS scroll-margin reserves space for a sticky header.
   */
  scrollIntoViewOnMobile() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    requestAnimationFrame(() => {
      this.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    });
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
